/* global setInterval, clearInterval */
// focus-timer.tsx — D35 Focus Timer floating card.
// Device-local countdown, NOT synced. Persists to localStorage for cross-reload resume.
// Derives remaining from timestamps each tick (not decrementing state) — background-tab accurate.
// No animated/pulsing styles (respects reduced-motion).
import { useEffect, useRef, useState } from "react";
import type { Task } from "../types";
import {
  clearFocusTimerSession,
  getFocusTimerSession,
  setFocusTimerSession,
  type FocusTimerSession,
} from "../lib/app-prefs";
import { DEFAULT_FOCUS_MINUTES, elapsedFocusMinutes, formatMmSs, remainingSeconds, resolveDurationMin } from "./focus-timer-derive";

interface Props {
  task: Task;
  /** Called when user clicks "Xong" — marks task done. */
  onDone: (taskId: string) => void;
  /** Called when user clicks "Dừng" — stops timer without completing task. */
  onStop: () => void;
  /**
   * Optional: called just before onDone/onStop clears the session.
   * Reports the elapsed focus minutes for this session so callers can
   * accumulate the time onto the task's loggedMinutes field.
   * Only fired when minutes > 0.
   */
  onLogTime?: (taskId: string, minutes: number) => void;
}

export function FocusTimer({ task, onDone, onStop, onLogTime }: Props) {
  // Resume from localStorage if same task in progress, otherwise start fresh.
  const [session, setSession] = useState<FocusTimerSession>(() => {
    const saved = getFocusTimerSession();
    if (saved && saved.taskId === task.id) return saved;
    const fresh: FocusTimerSession = {
      taskId: task.id,
      title: task.title,
      durationMin: resolveDurationMin(task.estimateMinutes),
      startedAt: Date.now(),
      pausedAccum: 0,
    };
    setFocusTimerSession(fresh);
    return fresh;
  });

  // Tick counter drives re-render; remaining is derived from timestamps each tick.
  const [tick, setTick] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isPaused = session.pausedAt != null;
  const nowMs = Date.now();
  const remaining = remainingSeconds(
    session.startedAt,
    session.durationMin,
    session.pausedAccum,
    nowMs,
    session.pausedAt,
  );
  const expired = remaining === 0;

  useEffect(() => {
    if (isPaused || expired) {
      if (intervalRef.current != null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    intervalRef.current = setInterval(() => {
      setTick((n) => n + 1);
    }, 1000);
    return () => {
      if (intervalRef.current != null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPaused, expired]);

  const notifiedRef = useRef(false);
  useEffect(() => {
    if (expired && !notifiedRef.current) {
      notifiedRef.current = true;
      try {
        if (typeof window !== "undefined" && window.Notification?.permission === "granted") {
          new window.Notification("Hết phiên tập trung", { body: session.title });
        }
      } catch {
        // Notification API not available or blocked — silently ignore.
      }
    }
  }, [expired, session.title]);

  function handlePauseResume() {
    if (isPaused) {
      // Resume: add the pause duration to accumulator, clear pausedAt.
      const pausedDurationMs = Date.now() - session.pausedAt!;
      const resumed: FocusTimerSession = {
        ...session,
        pausedAccum: session.pausedAccum + pausedDurationMs,
        pausedAt: undefined,
      };
      setFocusTimerSession(resumed);
      setSession(resumed);
    } else {
      // Pause: record when we paused.
      const paused: FocusTimerSession = { ...session, pausedAt: Date.now() };
      setFocusTimerSession(paused);
      setSession(paused);
    }
  }

  function handleStop() {
    // Report elapsed before clearing the session so the caller can accumulate time.
    const mins = elapsedFocusMinutes(
      session.startedAt,
      session.durationMin,
      session.pausedAccum,
      Date.now(),
      session.pausedAt,
    );
    clearFocusTimerSession();
    if (onLogTime && mins > 0) onLogTime(session.taskId, mins);
    onStop();
  }

  function handleDone() {
    // Report elapsed before clearing the session so the caller can accumulate time.
    const mins = elapsedFocusMinutes(
      session.startedAt,
      session.durationMin,
      session.pausedAccum,
      Date.now(),
      session.pausedAt,
    );
    clearFocusTimerSession();
    if (onLogTime && mins > 0) onLogTime(session.taskId, mins);
    onDone(session.taskId);
  }

  return (
    <div
      role="region"
      aria-label="Bộ đếm tập trung"
      className="fixed bottom-24 right-4 z-40 w-72 rounded-2xl bg-white shadow-xl ring-1 ring-ink-200/60 lg:bottom-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between rounded-t-2xl bg-brand-50 px-4 py-2.5">
        <span className="text-[11px] font-bold uppercase tracking-widest text-brand-600">
          Tập trung
        </span>
        <span className="max-w-[160px] truncate text-[12px] font-semibold text-ink-700">
          {session.title}
        </span>
      </div>

      {/* Timer display */}
      <div className="px-4 py-4 text-center">
        {expired ? (
          <p className="font-display text-2xl font-bold text-emerald-600">Hết giờ 🎉</p>
        ) : (
          <p
            data-tick={tick}
            className={`font-display text-4xl font-bold tabular-nums ${
              isPaused ? "text-ink-400" : "text-ink-900"
            }`}
          >
            {formatMmSs(remaining)}
          </p>
        )}
        <p className="mt-1 text-[11px] text-ink-400">
          {session.durationMin === DEFAULT_FOCUS_MINUTES && !task.estimateMinutes
            ? "25 phút (mặc định)"
            : `${session.durationMin} phút`}
        </p>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 px-4 pb-4">
        {expired ? (
          <>
            <button
              onClick={handleDone}
              className="flex-1 rounded-xl bg-emerald-500 px-3 py-2 text-[13px] font-bold text-white hover:bg-emerald-600 transition"
            >
              ✓ Xong
            </button>
            <button
              onClick={handleStop}
              className="rounded-xl border border-ink-200 px-3 py-2 text-[13px] font-semibold text-ink-600 hover:bg-ink-50 transition"
            >
              Đóng
            </button>
          </>
        ) : (
          <>
            <button
              onClick={handlePauseResume}
              className="flex-1 rounded-xl border border-brand-200 bg-brand-50 px-3 py-2 text-[13px] font-bold text-brand-700 hover:bg-brand-100 transition"
            >
              {isPaused ? "Tiếp tục" : "Tạm dừng"}
            </button>
            <button
              onClick={handleDone}
              className="rounded-xl bg-emerald-500 px-3 py-2 text-[13px] font-bold text-white hover:bg-emerald-600 transition"
            >
              ✓ Xong
            </button>
            <button
              onClick={handleStop}
              className="rounded-xl border border-ink-200 px-3 py-2 text-[13px] font-semibold text-ink-600 hover:bg-ink-50 transition"
            >
              Dừng
            </button>
          </>
        )}
      </div>
    </div>
  );
}
